import { Button, Stack } from "@mui/material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { useDispatch, useSelector } from "react-redux";
import { setModalOpen } from "../../../../../features/brandConfigurationReportSlice";
import { selectUserData, selectUserToken } from "../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import BrandDirectorySearch from "../BrandDirectorySearch";
import BrandsFilterItems from "../BrandsFilterItems";
import { ensureRunProcessSupportData } from "../utils/ensureRunProcessSupportData";

interface Props {
  rows: any[];
  selectedRows: number[];
  selectedBrandNames: string[];
  setSelectedBrandNames: (value: string[]) => void;
  selectedStatus: string[] | null;
  setSelectedStatus: (value: string[] | null) => void;
  selectedAffiliateSystem: string[] | null;
  setSelectedAffiliateSystem: (value: string[] | null) => void;
  selectedCompany: string[] | null;
  setSelectedCompany: (value: string[] | null) => void;
  selectedWebsite: string[] | null;
  setSelectedWebsite: (value: string[] | null) => void;
  clearFilters: () => void;
}

// Reuses the exact "Run Processes" bulk-button pattern from
// BrandsFilterCard.tsx (legacy data_collection/brand_data module) — same
// setModalOpen({isOpen, data:[...]}) dispatch into brandConfigurationReportSlice,
// which RunBrandData.tsx already reads.
const AllBrandsFilterCard = ({
  rows,
  selectedRows,
  selectedBrandNames,
  setSelectedBrandNames,
  ...filterProps
}: Props) => {
  const dispatch = useDispatch();
  const response = useSelector(selectUserData);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const hasUpdateBrandDataPermission = CheckPermissions(
    response?.data?.products?.data_collection,
    "brands",
    "update_brand_data",
  );

  // Run Processes only makes sense for brands that are actually runnable.
  // If any currently-selected brand is in a non-"ready" status
  // (not_ready / config_issue / account_issue / not_connected — the same
  // statuses that hide the per-row "Run Brand" quick action in
  // BrandsQuickActions.tsx), disable the bulk button too rather than letting
  // the wizard open for a brand it can't run.
  const NON_RUNNABLE_STATUSES = ["not_ready", "config_issue", "account_issue", "not_connected"];
  const hasNonRunnableSelection = selectedRows.some((brandId) => {
    const brand = rows.find((row: any) => row.brand_id === brandId);
    return brand && NON_RUNNABLE_STATUSES.includes(brand.status);
  });

  const handleRunProcesses = () => {
    if (selectedRows.length === 0) return;
    ensureRunProcessSupportData(oauthTokenHeader);
    const selectedBrandData = selectedRows.map((brandId) => {
      const brand = rows.find((row: any) => row.brand_id === brandId);
      return {
        brandId,
        brandName: brand?.brand_name ?? null,
        // Not available in the Brands query (5729017198018560) — "By Report"
        // mode will have no options for brands opened from this page; "By
        // Flow" still works via fetcherConfigData (see AllBrandsTable).
        row_output_table_data: [],
      };
    });
    dispatch(setModalOpen({ isOpen: true, data: selectedBrandData }));
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      sx={{ width: "100%" }}
    >
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <BrandDirectorySearch
          rows={rows}
          value={selectedBrandNames}
          onChange={setSelectedBrandNames}
        />
        <BrandsFilterItems rows={rows} {...filterProps} />
      </Stack>
      <Button
        variant="contained"
        color="secondary"
        endIcon={<PlayCircleIcon />}
        data-tracker="787"
        disabled={
          selectedRows.length === 0 ||
          !hasUpdateBrandDataPermission ||
          hasNonRunnableSelection
        }
        onClick={handleRunProcesses}
      >
        Run Processes
      </Button>
    </Stack>
  );
};

export default AllBrandsFilterCard;
